import React, { useEffect, useRef } from 'react';
import { init, registerOverlay } from 'klinecharts'



  // === custom overlay: tradeBadgeV1 ===
let _overlayReady = false;
function ensureTradeOverlay() {
  if (_overlayReady) return;
  registerOverlay({
    name: 'tradeBadgeV1',
    totalStep: 1,
    needDefaultPointFigure: false,
    createPointFigures: ({ coordinates, overlay }) => {
      const { x, y } = coordinates[0];

      // Данные управляют видом метки
      const ed   = overlay?.extendData || {};
      const text = String(ed.text ?? '');                // строка цены, например "$1.2345"
      const dir  = (ed.dir === 'down') ? 'down' : 'up';  // 'up' | 'down'
      const color = ed.color || '#9ca3af';               // цвет стрелки/текста

      // Геометрия: треугольник-стрелка и текст справа
      const size = 10;   // половина высоты треугольника
      const dx   = 8;    // отступ текста вправо от стрелки

      // Точки треугольника
        const triPoints = (dir === 'up')
        ? [
            [x, y - size],
            [x - size, y + size],
            [x + size, y + size]
          ]
        : [
            [x, y + size],
            [x - size, y - size],
            [x + size, y - size]
          ];

      return [
         // ПРОВЕРКА: простой круг вместо стрелки
        {
          type: 'circle',
          attrs: { x, y, r: 5 },
          styles: { style: 'fill', color },
          ignoreEvent: true
        },
        // Текст цены справа от круга
        {
          type: 'text',
          attrs: { x: x + 14, y, align: 'left', baseline: 'middle', text },
          styles: { color },
          ignoreEvent: true
        }
      ];
    },
  });
  _overlayReady = true;
}

  export default function TradeChart({ candles = [], markers = [], height = 360 }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const roRef = useRef(null);
  const disposedRef = useRef(false);
  const prevLenRef = useRef(0);
  const lastTsRef = useRef(null);
  const markerIdsRef = useRef([]);
  const overlayIdsRef = useRef([]);


  // Инициализация / очистка
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    disposedRef.current = false;

    try {
      const chart = init(el, {
        devicePixelRatio:
        (typeof window !== 'undefined' && window.devicePixelRatio)
          ? Math.max(1, window.devicePixelRatio)
          : 1,
        styles: {
          grid: {
            show: true,
            horizontal: { color: '#222' },
            vertical: { color: '#222' },
          },
          candle: {
            type: 'candle_solid',
            bar: { upColor: '#26a69a', downColor: '#ef4444' },
            wick: { upColor: '#26a69a', downColor: '#ef4444' },
          },
          xAxis: { line: { color: '#333' }, tickText: { color: '#d1d5db' } },
          yAxis: { line: { color: '#333' }, tickText: { color: '#d1d5db' } },
        },
      });
      ensureTradeOverlay();

      chartRef.current = chart;
      // Жёстко выключаем встроенные серые метки open/close/high/low/last
      try {
        chart.setStyles({ candle: { priceMark: { show: true } } });
      } catch {}
      try { chart.setPriceVolumePrecision?.(8, 2); } catch {}

      // Остальное — по желанию (не влияет на количество знаков):
      try { chart.setBarSpace(12); } catch {}
      try { chart.setOffsetRightDistance(40); } catch {}
      try { chart.scrollToRealTime(); } catch {}

      // первичная установка размера
      try {
        const w = Math.max(0, Math.round(el.clientWidth || 0));
        const h = Math.max(0, Math.round(height || 360));
        chart.resize(w, h);
      } catch {}

      // безопасный ResizeObserver
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => {
          if (!containerRef.current || !chartRef.current || disposedRef.current) return;
          try {
            const cw = Math.max(0, Math.round(containerRef.current.clientWidth || 0));
            const ch = Math.max(0, Math.round(height || 360));
            chartRef.current.resize(cw, ch);
          } catch {}
        });
        ro.observe(el);
        roRef.current = ro;
      }
    } catch (e) {
      // тихо логируем в консоль, чтобы не падал React
      console.error('TradeChart init error (safe):', e);
    }

    return () => {
      disposedRef.current = true;
      try {
        if (roRef.current && containerRef.current) {
          try { roRef.current.unobserve(containerRef.current); } catch {}
          try { roRef.current.disconnect(); } catch {}
        }
      } catch {}
      roRef.current = null;

      try {
        if (chartRef.current) {
          chartRef.current.dispose();
        }
      } catch {}
      chartRef.current = null;
    };
  }, [height]);

  // Обновление свечей
  useEffect(() => {
  if (!chartRef.current || disposedRef.current) return;
  try {
    const data = (Array.isArray(candles) ? candles : []).map(c => ({
      // klinecharts ждёт миллисекунды в timestamp
      timestamp: Math.trunc(Number(c.time) * 1000),
      open: Number(c.open),
      high: Number(c.high),
      low: Number(c.low),
      close: Number(c.close),
      volume: Number(c.volume || 0),
    }));
    if (!data.length) return;

    // 1) Первый заход или резкая смена данных → полная установка
    if (!prevLenRef.current || data.length < prevLenRef.current) {
      chartRef.current.applyNewData(data);
    } else {
      // 2) Обычный рилтайм: обновляем/добавляем только последний бар
      const last = data[data.length - 1];
      chartRef.current.updateData(last); // same ts → обновит; больше → добавит новый бар
    }

    prevLenRef.current = data.length;
    lastTsRef.current = data[data.length - 1]?.timestamp ?? null;
  } catch (e) {
    console.warn('updateData failed (safe):', e);
  }
}, [candles]);



    // Маркеры трейдера: используем кастомный overlay tradeBadgeV1
useEffect(() => {
  const chart = chartRef.current;
  if (!chart || !Array.isArray(markers)) return;

  // убрать старые оверлеи
  try {
    if (overlayIdsRef.current && overlayIdsRef.current.length) {
      overlayIdsRef.current.forEach(id => {
        try { chart.removeOverlay(id) } catch {}
      });
    } else {
      chart.removeAllOverlay?.();
    }
  } catch {}
  overlayIdsRef.current = [];

  const ids = [];
  for (const m of markers) {
    // время → миллисекунды
    const t = Number(m?.time ?? m?.timeSec ?? m?.timeMs);
    const tsMs = Number.isFinite(t)
      ? (t > 1e12 ? Math.trunc(t) : Math.trunc(t * 1000))
      : NaN;

    // цена сделки
    const p = Number(m?.price);
    if (!Number.isFinite(tsMs) || !Number.isFinite(p)) continue;

    // направление сделки
    const sideStr = String(m?.side || '').toLowerCase();
    const label = sideStr === 'buy' ? 'OPEN' : (sideStr === 'sell' ? 'CLOSE' : 'TRADE');

    // создаём overlay через нашу кастомку tradeBadgeV1
    const created = chart.createOverlay({
      name: 'tradeBadgeV1',
      lock: true,
      paneId: 'candle_pane',
      zLevel: 20,
      points: [{ timestamp: tsMs, value: p }],
      extendData: {
        text: `${label} @ ${p.toFixed(6)}`,
        dir: sideStr === 'buy' ? 'up' : 'down',
        color: sideStr === 'buy' ? '#22c55e' : (sideStr === 'sell' ? '#ef4444' : '#9ca3af')
      }
    });

    if (created?.id) ids.push(created.id);
  }
  overlayIdsRef.current = ids;
}, [markers]);


  return (
  <div
    ref={containerRef}
    style={{ width: '100%', height, background: '#111827', borderRadius: 6 }}
  />
);
}
